import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Autocomplete, MenuItem,
  Paper, Typography, Alert, InputAdornment,
} from '@mui/material';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (v) => (v === '' || v == null ? 0 : Number(v));

// A line still missing its price (required before the contract is sent or signed)
export const needsPrice = (item) => !(Number(item.unitPrice) > 0) || !(Number(item.quantity) > 0);

// Same arithmetic as the server (computeItemTotals)
export function itemTotals({ quantity, unitPrice, discountType, discountValue, taxRate }) {
  const subtotal = num(quantity) * num(unitPrice);
  const discountAmount = discountType === 'percent' ? (subtotal * num(discountValue)) / 100 : Math.min(num(discountValue), subtotal);
  const taxAmount = ((subtotal - discountAmount) * num(taxRate)) / 100;
  return { subtotal, discountAmount, taxAmount, lineTotal: subtotal - discountAmount + taxAmount };
}

const EMPTY = { quantity: '1', unitPrice: '', discountType: 'percent', discountValue: '0', taxRate: '18', notes: '' };

// Add a service line to a contract, or edit one: quantity, unit price, discount (% or ₹) and
// tax, with the line total worked out as you type. Without `canPrice` (a family) only the
// quantity and notes can be set; prices come from the catalog / the admin.
export default function ContractItemDialog({ open, onClose, onSave, item, services = [], canPrice = true, saving = false, error = '' }) {
  const editing = Boolean(item);
  const [svc, setSvc] = useState(null);
  const [f, setF] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    setSvc(null);
    setF(item ? {
      quantity: String(item.quantity ?? 1), unitPrice: String(item.unitPrice ?? ''), discountType: item.discountType || 'percent',
      discountValue: String(item.discountValue ?? 0), taxRate: String(item.taxRate ?? 18), notes: item.notes || '',
    } : EMPTY);
  }, [open, item]);

  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const pickService = (s) => {
    setSvc(s);
    // Start from the catalog price and tax; the admin/vendor can change them
    if (s) setF(x => ({ ...x, unitPrice: String(s.basePrice ?? ''), taxRate: String(s.taxRate ?? 18) }));
  };
  const t = itemTotals(f);
  const blank = (v) => v === '' || v == null;
  // Quantity, unit price, discount and tax are all required (0 is fine for discount and tax)
  const problem = (!editing && !svc) ? 'Choose a service'
    : !(num(f.quantity) > 0) ? 'Enter a quantity greater than zero'
      : canPrice && !(num(f.unitPrice) > 0) ? 'Enter a unit price greater than zero'
      : canPrice && blank(f.discountValue) ? 'Enter the discount (0 if none)'
      : canPrice && blank(f.taxRate) ? 'Enter the tax rate (0 if none)'
      : num(f.unitPrice) < 0 || num(f.taxRate) < 0 || num(f.discountValue) < 0 ? 'Amounts cannot be negative'
        : f.discountType === 'percent' && num(f.discountValue) > 100 ? 'Discount cannot be more than 100%'
          : svc?.maxDiscount != null && f.discountType === 'percent' && num(f.discountValue) > Number(svc.maxDiscount) && Number(svc.maxDiscount) > 0
            ? `The catalog allows at most ${svc.maxDiscount}% discount on this service` : '';

  const save = () => onSave({
    ...(editing ? {} : { serviceId: svc.id, serviceName: svc.name, serviceCategory: svc.category, unit: svc.unit }),
    quantity: num(f.quantity),
    ...(canPrice && { unitPrice: num(f.unitPrice), discountType: f.discountType, discountValue: num(f.discountValue), taxRate: num(f.taxRate) }),
    notes: f.notes.trim() || null,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Edit ${item.serviceName}` : 'Add service to contract'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {!editing && (
            <Autocomplete
              options={services}
              getOptionLabel={(s) => `${s.name} — ₹${Number(s.basePrice).toLocaleString('en-IN')} (${s.unit})`}
              groupBy={(s) => s.category}
              value={svc}
              onChange={(_, v) => pickService(v)}
              renderInput={(params) => <TextField {...params} label="Service" fullWidth />}
            />
          )}
          {editing && <Typography variant="body2" color="text.secondary">{item.serviceCategory} · {item.unit}</Typography>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Quantity *" type="number" value={f.quantity} onChange={set('quantity')} size="small" fullWidth inputProps={{ min: 0, step: 'any' }} />
            <TextField label={canPrice ? 'Unit price *' : 'Unit price'} type="number" value={f.unitPrice} onChange={set('unitPrice')} size="small" fullWidth disabled={!canPrice}
              inputProps={{ min: 0, step: 'any' }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label="Discount type" value={f.discountType} onChange={set('discountType')} size="small" fullWidth disabled={!canPrice}>
              <MenuItem value="percent">Percent (%)</MenuItem>
              <MenuItem value="amount">Amount (₹)</MenuItem>
            </TextField>
            <TextField label={`${f.discountType === 'percent' ? 'Discount (%)' : 'Discount (₹)'}${canPrice ? ' *' : ''}`} type="number" value={f.discountValue} onChange={set('discountValue')}
              size="small" fullWidth disabled={!canPrice} inputProps={{ min: 0, step: 'any' }} />
            <TextField label={canPrice ? 'Tax / GST (%) *' : 'Tax / GST (%)'} type="number" value={f.taxRate} onChange={set('taxRate')} size="small" fullWidth disabled={!canPrice}
              inputProps={{ min: 0, step: 'any' }} />
          </Stack>
          {!canPrice && (
            <Typography variant="caption" color="text.secondary">Prices, discounts and tax are set by the admin or the vendor.</Typography>
          )}
          <TextField label="Notes" value={f.notes} onChange={set('notes')} size="small" fullWidth multiline minRows={2} />
          <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Typography variant="body2">Subtotal <strong>{inr(t.subtotal)}</strong></Typography>
              <Typography variant="body2" color="success.main">Discount −{inr(t.discountAmount)}</Typography>
              <Typography variant="body2" color="warning.main">Tax +{inr(t.taxAmount)}</Typography>
              <Typography variant="body2" sx={{ ml: 'auto' }}>Line total <strong>{inr(t.lineTotal)}</strong></Typography>
            </Stack>
          </Paper>
          {(problem || error) && <Alert severity={error ? 'error' : 'warning'}>{error || problem}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={Boolean(problem) || saving}>{editing ? 'Save' : 'Add to contract'}</Button>
      </DialogActions>
    </Dialog>
  );
}
